import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, effectiveTier, planLabel } from "@/lib/AuthContext";
import { LayoutDashboard, Video, Image as ImageIcon, FolderOpen, CreditCard, LogOut, ShieldCheck, Mic, Key, Coins, Sparkles, Wand2, Briefcase, Megaphone, PenTool, Film, BookOpen } from "lucide-react";
import ServiceHealthBanner from "@/components/dashboard/ServiceHealthBanner";
import api from "@/lib/api";

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const tier = effectiveTier(user);
  const isTrial = !tier;
  const trialRemaining = Math.max(0, 2 - (user?.trial_generations_used || 0));

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/templates", label: "Templates", icon: Sparkles, testId: "nav-templates" },
    { to: "/director", label: "Director", icon: Wand2, testId: "nav-director" },
    { to: "/generate/ad", label: "Ad Generator", icon: Megaphone, testId: "nav-ad" },
    { to: "/generate/commercial", label: "Multi-Shot Commercial", icon: Film, testId: "nav-commercial" },
    { to: "/magazine", label: "Magazine", icon: BookOpen, testId: "nav-magazine" },
    { to: "/wordsmith", label: "Wordsmith", icon: PenTool, testId: "nav-wordsmith" },
    { to: "/generate/logo", label: "Logo Generator", icon: Briefcase, testId: "nav-logo" },
    { to: "/generate/video", label: "Generate Video", icon: Video, testId: "nav-generate-video" },
    { to: "/generate/image", label: "Generate Image", icon: ImageIcon, testId: "nav-generate-image" },
    { to: "/generate/voiceover", label: "Voiceover", icon: Mic, testId: "nav-generate-voiceover" },
    { to: "/renders", label: "My Renders", icon: FolderOpen, testId: "nav-renders" },
    { to: "/credits", label: "Credits", icon: Coins, testId: "nav-credits" },
    { to: "/pricing", label: isTrial ? "Upgrade Plan" : "Manage Plan", icon: CreditCard, testId: "nav-pricing" },
    { to: "/settings/api-keys", label: "API Keys", icon: Key, testId: "nav-api-keys" },
  ];
  if (user?.role === "owner") {
    nav.push({ to: "/owner", label: "Switch to Admin", icon: ShieldCheck, testId: "nav-switch-admin" });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen luxe-bg text-[#F4F4F0]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex md:flex-col w-64 lg:w-72 min-h-screen border-r border-[#D4AF37]/20 bg-[#0a0a0a]/95 backdrop-blur-xl sticky top-0 sparkle-bed">
          <Link to="/" className="block px-6 py-8 border-b border-[#D4AF37]/15" data-testid="dashboard-brand-link">
            <span className="block text-[10px] tracking-[0.26em] uppercase text-[#D4AF37] font-semibold">
              Young Black Man Inc
            </span>
            <span className="block font-display text-2xl font-extrabold tracking-tight mt-1 gold-shimmer">Studios</span>
          </Link>

          {/* Plan card */}
          <div className="px-6 py-5 border-b border-[#D4AF37]/15">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-2">Current Plan</p>
            <p className="font-display text-2xl font-extrabold tracking-tight gold-shimmer" data-testid="dashboard-plan-label">
              {planLabel(user)}
            </p>
            {isTrial && (
              <p className="text-xs text-[#A0A0A0] mt-2" data-testid="dashboard-trial-remaining">
                <span className="text-[#F4F4F0] font-semibold">{trialRemaining}</span> of 2 free downloads left
              </p>
            )}
            {user?.role === "owner" && user?.impersonated_tier && (
              <p className="text-[10px] text-[#D4AF37]/80 uppercase tracking-wider mt-2">
                Impersonating: {user.impersonated_tier}
              </p>
            )}
          </div>

          <nav className="flex-1 px-3 py-6 space-y-1">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    active
                      ? "bg-[#121212] text-[#F4F4F0] border-l-2 border-[#D4AF37]"
                      : "text-[#A0A0A0] hover:text-[#F4F4F0] hover:bg-[#121212] border-l-2 border-transparent"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="px-6 py-5 border-t border-white/10">
            <p className="text-xs text-[#A0A0A0] mb-1 truncate" data-testid="dashboard-user-email">{user?.email}</p>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 text-xs text-[#A0A0A0] hover:text-[#D4AF37] mt-2 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#080808]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg tracking-tight">Studios</Link>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">{planLabel(user)}</span>
            <button onClick={handleLogout} data-testid="mobile-logout-button" className="text-[#A0A0A0]"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>

        <main className="flex-1 min-w-0 pt-16 md:pt-0">
          {/* Mobile nav row */}
          <div className="md:hidden flex gap-1 overflow-x-auto px-4 py-3 border-b border-white/10">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`text-xs px-3 py-2 whitespace-nowrap ${active ? "text-[#D4AF37] border-b border-[#D4AF37]" : "text-[#A0A0A0]"}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <ServiceHealthBanner />
            <CreditPill user={user} />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function CreditPill({ user }) {
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/credits/me").then(({ data }) => setBalance(data?.balance ?? 0)).catch(() => {});
  }, [user]);
  if (balance === null) return null;
  const low = balance < 50;
  return (
    <div
      data-testid="credit-pill"
      className={`mb-6 inline-flex items-center gap-3 px-4 py-2 border ${low ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37]"}`}
    >
      <Coins className="w-4 h-4" />
      <span className="font-mono text-sm font-bold">{balance.toLocaleString()}</span>
      <span className="text-[10px] uppercase tracking-wider">credits</span>
      <Link to="/credits" className="text-[10px] uppercase tracking-wider underline ml-2">
        {low ? "Top up" : "Wallet"}
      </Link>
    </div>
  );
}
